import React, { useEffect, useState, useMemo } from "react";
import { api, API_BASE } from "@/lib/api";
import Flag from "@/components/Flag";
import { useDateTimeFormat, useTz } from "@/lib/TzContext";
import { useI18n } from "@/lib/I18nContext";
import { teamLabel } from "@/lib/teamNames";
import { Download, CheckCircle2, Clock, Lock } from "lucide-react";
import { toast } from "sonner";

const STAGE_TABS_IDS = ["groups", "r32", "r16", "qf", "sf", "third", "final"];

export default function TodosPronosticos() {
  const [tab, setTab] = useState("marcadores");
  const { t } = useI18n();
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [myPreds, setMyPreds] = useState({ matches: [] });
  const [appState, setAppState] = useState({ predictions_published: false, stages: {} });

  const load = async () => {
    const [t, m, st, me] = await Promise.all([
      api.get("/tournament/teams"),
      api.get("/tournament/matches"),
      api.get("/app-state"),
      api.get("/predictions/me").catch(() => ({ data: { matches: [] } })),
    ]);
    setTeams(t.data);
    setMatches(m.data);
    setAppState(st.data);
    setMyPreds(me.data || { matches: [] });
  };

  useEffect(() => { load(); }, []);

  const teamByCode = useMemo(() => Object.fromEntries(teams.map((t) => [t.code, t])), [teams]);

  const downloadStageXlsx = async (stageId) => {
    const stageLabel = stageLabelFor(stageId, t);
    try {
      const token = localStorage.getItem("polla_token");
      const r = await fetch(`${API_BASE}/predictions/public-export.xlsx?stage=${encodeURIComponent(stageId)}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data?.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `polla-${stageId}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${t("todos.excelDownload")}: ${stageLabel}`);
    } catch (e) {
      toast.error(`${t("err.save")}: ${e.message}`);
    }
  };

  const STAGE_TABS = STAGE_TABS_IDS.map((id) => ({
    id,
    label: id === "groups" ? t("phase1.title") : t(`phase2.stages.${id}`),
    short: id === "groups" ? t("phase1.tabs.todos") : t(`phase2.short.${id}`),
  }));

  return (
    <div className="space-y-6" data-testid="todos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">{t("todos.kicker")}</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase leading-none">{t("todos.title")}</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 overflow-x-auto" data-testid="todos-tabs">
        <button
          onClick={() => setTab("marcadores")}
          data-testid="tab-marcadores"
          className={`px-4 py-3 text-xs uppercase tracking-widest whitespace-nowrap transition-colors -mb-px ${
            tab === "marcadores"
              ? "border-b-2 border-[var(--c-lime)] text-[var(--c-lime)] font-bold"
              : "text-zinc-400 hover:text-white"
          }`}
        >{t("todos.tabs.real")}</button>
        {STAGE_TABS.map((s) => {
          const published = !!(appState.stages && appState.stages[s.id]);
          return (
            <button
              key={s.id}
              onClick={() => setTab(s.id)}
              data-testid={`tab-${s.id}`}
              className={`px-4 py-3 text-xs uppercase tracking-widest whitespace-nowrap transition-colors -mb-px flex items-center gap-1.5 ${
                tab === s.id
                  ? "border-b-2 border-[var(--c-lime)] text-[var(--c-lime)] font-bold"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              <span>{s.short}</span>
              {published
                ? <CheckCircle2 size={11} className="text-[var(--c-lime)]" />
                : <Lock size={11} className="text-zinc-600" />}
            </button>
          );
        })}
      </div>

      {tab === "marcadores" ? (
        <RealResultsTable matches={matches} teamByCode={teamByCode} myPreds={myPreds} />
      ) : (
        <StageDownloadView
          stage={STAGE_TABS.find((s) => s.id === tab)}
          appState={appState}
          onDownload={() => downloadStageXlsx(tab)}
        />
      )}
    </div>
  );
}

function stageLabelFor(id, t) {
  if (id === "groups") return t("phase1.title");
  return t(`phase2.stages.${id}`);
}

function StageDownloadView({ stage, appState, onDownload }) {
  const { format } = useTz();
  const { t } = useI18n();
  const published = !!(appState.stages && appState.stages[stage.id]);
  const publishedAt = appState.predictions_published_at && stage.id === "groups";
  return (
    <div className="space-y-6">
      <div className={`p-4 border text-sm flex items-center gap-2 ${
        published
          ? "bg-[var(--c-lime)]/10 border-[var(--c-lime)] text-[var(--c-lime)]"
          : "bg-zinc-900 border-zinc-800 text-zinc-400"
      }`} data-testid={`publish-status-${stage.id}`}>
        {published
          ? <><CheckCircle2 size={16} /> {t("todos.publishedOn", { stage: stage.label, date: publishedAt ? format(appState.predictions_published_at, { year: "numeric", month: "short", day: "2-digit" }) : "" })}</>
          : <><Clock size={16} /> {t("todos.notPublishedYet", { stage: stage.label })}</>}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-8 text-center" data-testid={`stage-download-panel-${stage.id}`}>
        <Download className="mx-auto text-[var(--c-lime)] mb-3" size={28} />
        <h2 className="font-display text-2xl md:text-3xl uppercase leading-tight mb-2">{t("todos.excelHeader", { stage: stage.label })}</h2>
        <p className="text-zinc-300 text-base max-w-xl mx-auto mb-5 leading-relaxed">
          {t(published ? "todos.excelHelp.available" : "todos.excelHelp.locked", { stage: stage.label })}
        </p>
        <button
          onClick={onDownload}
          disabled={!published}
          data-testid={`stage-download-button-${stage.id}`}
          className={`inline-flex items-center gap-2 font-bold uppercase tracking-wider px-6 py-3 text-sm ${
            published
              ? "bg-[var(--c-lime)] text-black hover:bg-lime-300"
              : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
          }`}
        >
          {published
            ? (<><Download size={16} /> {t("todos.excelDownload")}</>)
            : (<><Lock size={16} /> {t("todos.excelLocked")}</>)}
        </button>
      </div>
    </div>
  );
}

function RealResultsTable({ matches, teamByCode, myPreds }) {
  const fmt = useDateTimeFormat();
  const { t, lang } = useI18n();
  const myPredByMatch = useMemo(
    () => Object.fromEntries((myPreds?.matches || []).map((p) => [p.match_id, p])),
    [myPreds]
  );
  const sorted = useMemo(
    () => [...matches].sort((a, b) => {
      const ka = new Date(a.kickoff || 0).getTime();
      const kb = new Date(b.kickoff || 0).getTime();
      return ka - kb || a.match_id.localeCompare(b.match_id);
    }),
    [matches]
  );

  if (sorted.length === 0) {
    return <div className="text-zinc-500 text-sm">{t("todos.excelLoading")}</div>;
  }

  return (
    <div className="space-y-3" data-testid="real-results-table">
      <div className="flex flex-wrap gap-3 text-xs items-center">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 bg-white" />
          <span className="text-zinc-300">{t("todos.legend.real")}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3" style={{ background: "var(--c-lime)" }} />
          <span className="text-zinc-300">{t("todos.legend.mine")}</span>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800">
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
          <div className="col-span-2">{t("todos.col.date")}</div>
          <div className="col-span-1">{t("todos.col.group")}</div>
          <div className="col-span-3">{t("todos.col.match")}</div>
          <div className="col-span-2 text-center">{t("todos.col.real")}</div>
          <div className="col-span-2 text-center">{t("todos.col.mine")}</div>
          <div className="col-span-2 text-right">{t("todos.col.city")}</div>
        </div>
      {sorted.map((m) => {
        const home = m.home || "—";
        const away = m.away || "—";
        const homeT = teamByCode[m.home];
        const awayT = teamByCode[m.away];
        const realPosted = m.result_home != null && m.result_away != null;
        const myPred = myPredByMatch[m.match_id];
        const myFilled = myPred && (myPred.home_score != null && myPred.away_score != null);
        return (
          <div key={m.match_id} className="border-b border-zinc-800" data-testid={`real-row-${m.match_id}`}>
            {/* Mobile-only metadata header: date · time · venue, white text on gray bar */}
            <div className="md:hidden bg-zinc-700 text-white px-4 py-2 flex items-start justify-between gap-3 text-[11px] font-mono-data" data-testid={`real-row-${m.match_id}-meta`}>
              <div className="flex flex-col leading-tight">
                <span className="uppercase tracking-wider">{m.kickoff ? fmt.shortYear(m.kickoff) : "—"}</span>
                <span className="uppercase tracking-wider text-zinc-200">{m.kickoff ? fmt.time(m.kickoff) : ""}</span>
              </div>
              <div className="text-right text-zinc-100 truncate max-w-[55%]">{m.venue || "—"}</div>
            </div>
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs md:text-sm items-center">
              <div className="hidden md:block md:col-span-2 font-mono-data text-zinc-400">
                <div>{m.kickoff ? fmt.shortYear(m.kickoff) : "—"}</div>
                <div className="text-zinc-600 text-[10px]">{m.kickoff ? fmt.time(m.kickoff) : ""}</div>
              </div>
              <div className="col-span-3 md:col-span-1 font-mono-data text-zinc-500 uppercase">
                {m.group ? t("phase1.tabs.group", { letter: m.group }) : m.round_label}
              </div>
              <div className="col-span-9 md:col-span-3 flex items-center gap-2 min-w-0">
                {homeT && <Flag code={home} flag={homeT?.flag} flagImageUrl={homeT?.flag_image_url} size={20} />}
                <span className="truncate">{teamLabel(home, homeT?.name, lang) || home}</span>
                <span className="text-zinc-600">vs</span>
                {awayT && <Flag code={away} flag={awayT?.flag} flagImageUrl={awayT?.flag_image_url} size={20} />}
                <span className="truncate">{teamLabel(away, awayT?.name, lang) || away}</span>
              </div>
              <div className="col-span-6 md:col-span-2 text-center font-mono-data">
                <div className="md:hidden text-[9px] uppercase tracking-widest text-zinc-500 mb-1">{t("todos.col.real")}</div>
                {realPosted ? (
                  <span className="text-white font-bold text-base">{m.result_home} - {m.result_away}</span>
                ) : (
                  <span className="text-zinc-600">{t("todos.notPlayed")}</span>
                )}
              </div>
              <div className="col-span-6 md:col-span-2 text-center font-mono-data">
                <div className="md:hidden text-[9px] uppercase tracking-widest text-zinc-500 mb-1">{t("todos.col.mine")}</div>
                {myFilled ? (
                  <span className="text-[var(--c-lime)] font-bold text-base">{myPred.home_score} - {myPred.away_score}</span>
                ) : (
                  <span className="text-zinc-600">{t("todos.noPrediction")}</span>
                )}
              </div>
              <div className="hidden md:block md:col-span-2 text-right text-[11px] text-zinc-300 truncate">
                {m.venue || "—"}
              </div>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
